import { Loader } from '@modules/common/components/Loader';
import {
  createInstance,
  enums,
  OptimizelyProvider,
} from '@optimizely/react-sdk';
import { useDownloadFeatureFlagDataFileQuery } from 'generated/graphql';
import { useEffect, useState } from 'react';
import AuthService from 'services/AuthService';
import { GetConfig } from 'v2/core/config';

type Props = {
  children: JSX.Element;
};

const optStaticInstance = createInstance({
  sdkKey: GetConfig().featureFlagKey,
  eventBatchSize: 10,
  eventFlushInterval: 1000,
  logLevel: enums.LOG_LEVEL.WARNING,
});

// TODO: move this over to service
const useGetOptimizelyInstance = () => {
  const [optInstance, setOptInstance] = useState(optStaticInstance);

  const { data, loading } = useDownloadFeatureFlagDataFileQuery({
    variables: {
      fileURL: `https://cdn.optimizely.com/datafiles/${
        GetConfig().featureFlagKey
      }.json`,
    },
    fetchPolicy: 'cache-and-network',
    skip: !AuthService.isAuthenticated(),
  });

  const dataFileB64 = data?.proxyFileDownload?.base64Data;

  useEffect(() => {
    if (!dataFileB64 || optInstance.getOptimizelyConfig()) {
      return;
    }

    const dataFile = atob(dataFileB64 ?? '');
    setOptInstance(
      createInstance({
        datafile: dataFile,
        eventBatchSize: 10,
        eventFlushInterval: 1000,
      }),
    );
  }, [dataFileB64, optInstance]);

  return { optInstance, loading };
};

/**
##TODO, refactor the order of Optimizely, User and Auth providers.
  
## Workaround to avoid caching Labels data due to the following issue. 
 
The conditional statement is added to stop rendering content until the Optimizely data gets resolved. 
However, this change has resulted in unintended consequences.

<code>
  if(loading){
    return null;
  }
</code>

Why we made that change?
The reason why this change was made is that removing the useGetTaxonomy API led to rendering the content before the Optimizely data was available. 
This caused the code to point to stale Optimizely data, leading to incorrect render states.

What happened?
The issue arises because the content is conditionally rendered inside the OptimizelyFeatureFlagProvider component based on the loading state of the Optimizely data. 
Due to the useDownloadFeatureFlagDataFileQuery fetch policy (cache-and-network), the loading prop alternates between "true" and "false" 
  -> as the component switches between rendering with cached data and rendering with updated data fetched from the network.

The problem is that as the content gets mounted, unmounted, and mounted quickly, 
the useQuery with a 'no-cache' policy is being ignored by the Apollo client, resulting in the caching of TrakedKeywords and Reasons. 
As a result, the cache size is huge, and cache evict operations take time, leading to performance issues.

 */
let isRenderedOnce = false;

const OptimizelyFeatureFlagProvider = ({ children }: Props) => {
  const { optInstance, loading } = useGetOptimizelyInstance();

  if (loading && !isRenderedOnce) {
    return <Loader />;
  }

  isRenderedOnce = true;

  return (
    <OptimizelyProvider optimizely={optInstance} timeout={500}>
      {children}
    </OptimizelyProvider>
  );
};

export default OptimizelyFeatureFlagProvider;
